import { VarianteChip } from '../ui/primitivos/Chip';

/**
 * Rótulo do status de uma execução de sincronização (at-67, frente D/D5).
 *
 * 🔴 **Existe porque o mapa estava copiado TRÊS vezes, idêntico e com emoji** —
 * `ModalSincronizarCotas`, `ModalSincronizarProdutos` e `ModalDescobertaAssociados`
 * declaravam cada um o seu `STATUS_LABEL` com `⏳ Em andamento`, `✅ Concluída`,
 * `❌ Falhou`, `⚠️ Interrompida`. Emoji em interface é proibido no projeto (regra
 * de Victor, 06/09/2026), e regra repetida em componente é divergência com data
 * marcada (convenção da at-24).
 *
 * ⚠️ **A variante `pendente` (âmbar) para `INTERROMPIDO`, não `perigo`.** Uma
 * execução interrompida (heartbeat parado > 3 min, instância reciclada) **não é
 * erro do sistema**: é trabalho a retomar, e o próprio cron a retoma na janela
 * seguinte. Vermelho fica para `FALHA`, que é a execução que terminou mal. Mesmo
 * critério da spec 02 §2.3, que o `Chip` documenta.
 *
 * 📌 O vocabulário é o das tabelas `sga_cota_sync_run`, `sga_produto_sync_run` e
 * `sga_descoberta_run` — as três compartilham os enums `SgaCotaSyncStatus` e
 * `SgaCotaSyncOrigem` no schema (`05-database.md`).
 */

export interface RotuloStatusExecucao {
  texto: string;
  variante: VarianteChip;
}

const POR_STATUS: Record<string, RotuloStatusExecucao> = {
  EM_ANDAMENTO: { texto: 'Em andamento', variante: 'neutro' },
  CONCLUIDO: { texto: 'Concluída', variante: 'ok' },
  FALHA: { texto: 'Falhou', variante: 'perigo' },
  INTERROMPIDO: { texto: 'Interrompida', variante: 'pendente' },
};

/**
 * ⚠️ Status desconhecido devolve o **próprio valor** em `neutro`, nunca uma
 * string vazia: se o backend passar a emitir um status novo, a tela mostra o que
 * recebeu em vez de um chip mudo — o mesmo princípio do `situacao` do SGA, que o
 * SAAC relata sem interpretar (at-32).
 */
export function rotuloStatusExecucao(status: string): RotuloStatusExecucao {
  return POR_STATUS[status] ?? { texto: status, variante: 'neutro' };
}

/** Estado da `BarraProgresso` correspondente ao status da execução. */
export function estadoDaBarra(status: string): 'em-andamento' | 'erro' | 'concluido' {
  if (status === 'FALHA' || status === 'INTERROMPIDO') return 'erro';
  if (status === 'CONCLUIDO') return 'concluido';
  return 'em-andamento';
}

/** "automático (cron)" / "manual" — o `origem` das runs. */
export function rotuloOrigemExecucao(origem: string): string {
  return origem === 'CRON' ? 'automático (cron)' : 'manual';
}
